import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils';

const appBarVariants = cva(
  'w-full flex items-center justify-between transition-colors duration-200',
  {
    variants: {
      position: {
        static: 'relative',
        fixed: 'fixed top-0 left-0 right-0 z-50',
        absolute: 'absolute top-0 left-0 right-0 z-40',
        sticky: 'sticky top-0 z-40',
      },
      color: {
        default: 'bg-background border-b border-border',
        primary: 'bg-bhanzu-orange text-white',
        secondary: 'bg-blue-chaos text-white',
        transparent: 'bg-transparent',
        dark: 'bg-gray-900 text-white',
        light: 'bg-gray-50 text-gray-900',
      },
      elevation: {
        0: 'shadow-none',
        1: 'shadow-sm',
        2: 'shadow',
        3: 'shadow-md',
        4: 'shadow-lg',
        8: 'shadow-xl',
        12: 'shadow-2xl',
      },
      size: {
        sm: 'h-12 px-4',
        default: 'h-16 px-6',
        lg: 'h-20 px-8',
      },
    },
    defaultVariants: {
      position: 'static',
      color: 'default',
      elevation: 1,
      size: 'default',
    },
  }
);

const toolbarVariants = cva('w-full flex items-center justify-between', {
  variants: {
    variant: {
      default: 'gap-4',
      dense: 'gap-2',
      spacious: 'gap-6',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface AppBarProps extends React.HTMLAttributes<HTMLElement> {
  position?: 'static' | 'fixed' | 'absolute' | 'sticky';
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'transparent'
    | 'dark'
    | 'light';
  elevation?: 0 | 1 | 2 | 3 | 4 | 8 | 12;
  size?: 'sm' | 'default' | 'lg';
  component?: React.ElementType;
}

export interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'dense' | 'spacious';
}

const AppBar = React.forwardRef<HTMLElement, AppBarProps>(
  (
    {
      className,
      position,
      color,
      elevation,
      size,
      component: Component = 'header',
      children,
      ...props
    },
    ref
  ) => {
    return (
      <Component
        ref={ref}
        className={cn(
          appBarVariants({ position, color, elevation, size }),
          className
        )}
        role="banner"
        {...props}
      >
        {children}
      </Component>
    );
  }
);

AppBar.displayName = 'AppBar';

const Toolbar = React.forwardRef<HTMLDivElement, ToolbarProps>(
  ({ className, variant, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(toolbarVariants({ variant }), className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Toolbar.displayName = 'Toolbar';

export { AppBar, Toolbar, appBarVariants, toolbarVariants };
